import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import RedeemRoundedIcon from "@mui/icons-material/RedeemRounded";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { formatMoney } from "@bw/shared/utils";
import { Page } from "../../common/components/Page";
import { rewardsApi, type RewardsSummary } from "./api";

const services = {
  court_reservation: {
    label: "Court reservation",
    to: (facilityId: string) => `/book/${encodeURIComponent(facilityId)}`,
  },
  open_play: {
    label: "Drop-in",
    to: (facilityId: string) => `/dropin/${encodeURIComponent(facilityId)}`,
  },
  classes: {
    label: "Classes & clinics",
    to: (facilityId: string) => `/classes/${encodeURIComponent(facilityId)}`,
  },
  coaching: {
    label: "Private coaching",
    to: (facilityId: string) =>
      `/facility/${encodeURIComponent(facilityId)}/coaches`,
  },
} as const;

const statusLabels = {
  available: "Ready to use",
  reserved: "In checkout",
  redeemed: "Used",
  revoked: "Corrected",
  expired: "Expired",
  pending: "Visit needed",
  qualified: "Reward earned",
  corrected: "Corrected",
} as const;

function benefit(grant: RewardsSummary["grants"][number]): string {
  if (grant.discount_type === "fixed_amount") {
    return `${formatMoney(grant.discount_amount, grant.currency)} off`;
  }
  const percent = Number(grant.discount_percent ?? 0).toLocaleString(
    undefined,
    {
      maximumFractionDigits: 2,
    },
  );
  const maximum = grant.maximum_discount
    ? `, up to ${formatMoney(grant.maximum_discount, grant.currency)}`
    : "";
  return `${percent}% off${maximum}`;
}

export function RewardsPage() {
  const client = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const shareCampaign = searchParams.get("share");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState<string | null>(null);
  const rewards = useQuery({
    queryKey: ["rewards", "mine"],
    queryFn: rewardsApi.mine,
  });
  const claim = useMutation({
    mutationFn: rewardsApi.claimReferral,
    onSuccess: async () => {
      setCode("");
      await client.invalidateQueries({ queryKey: ["rewards", "mine"] });
    },
  });
  const createReferral = useMutation({
    mutationFn: rewardsApi.referralCode,
    onSuccess: async () => {
      setSearchParams({}, { replace: true });
      await client.invalidateQueries({ queryKey: ["rewards", "mine"] });
    },
  });

  const copy = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(value);
    } catch {
      setCopied(null);
    }
  };

  return (
    <Page title="Rewards & referrals">
      <Stack spacing={3}>
        <Box>
          <Typography color="text.secondary">
            Earn facility-funded rewards from verified visits. Rewards are
            facility-specific, non-transferable, and have no cash value.
          </Typography>
        </Box>

        {shareCampaign && rewards.data?.referral_participation_allowed ? (
          <Alert
            severity="info"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={createReferral.isPending}
                onClick={() => createReferral.mutate(shareCampaign)}
              >
                Create my code
              </Button>
            }
          >
            Create a share code for this facility offer. The code itself has no
            value; rewards unlock only after verified fulfillment.
          </Alert>
        ) : null}
        {createReferral.isError ? (
          <Alert severity="error">
            {createReferral.error instanceof Error
              ? createReferral.error.message
              : "A referral code could not be created."}
          </Alert>
        ) : null}

        {rewards.data?.referral_participation_allowed ? (
          <Card
            variant="outlined"
            component="section"
            aria-labelledby="claim-referral-heading"
          >
            <CardContent>
              <Stack
                component="form"
                spacing={1.5}
                onSubmit={(event) => {
                  event.preventDefault();
                  if (code.trim()) claim.mutate(code.trim());
                }}
              >
                <Typography
                  id="claim-referral-heading"
                  component="h2"
                  variant="h6"
                >
                  Have a referral code?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Claiming links the referral. Nobody earns a reward until you
                  complete the verified service shown in the offer.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    label="Referral code"
                    value={code}
                    onChange={(event) =>
                      setCode(event.target.value.toUpperCase())
                    }
                    slotProps={{ htmlInput: { maxLength: 24 } }}
                    fullWidth
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={!code.trim() || claim.isPending}
                    sx={{ minWidth: 132 }}
                  >
                    Claim code
                  </Button>
                </Stack>
                {claim.isSuccess ? (
                  <Alert severity="success">
                    Referral linked. Complete the verified service to unlock the
                    reward.
                  </Alert>
                ) : null}
                {claim.isError ? (
                  <Alert severity="error">
                    {claim.error instanceof Error
                      ? claim.error.message
                      : "This referral code could not be claimed."}
                  </Alert>
                ) : null}
              </Stack>
            </CardContent>
          </Card>
        ) : null}

        {rewards.isLoading ? (
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ alignItems: "center" }}
            role="status"
          >
            <CircularProgress size={22} />
            <Typography>Loading rewards…</Typography>
          </Stack>
        ) : null}
        {rewards.isError ? (
          <Alert severity="error">
            Rewards are temporarily unavailable. Your earned records are
            unchanged.
          </Alert>
        ) : null}
        {rewards.data ? (
          <>
            <Stack
              component="section"
              spacing={1.5}
              aria-labelledby="available-rewards-heading"
            >
              <Typography
                id="available-rewards-heading"
                component="h2"
                variant="h5"
              >
                Your rewards
              </Typography>
              {rewards.data.grants.length === 0 ? (
                <Card variant="outlined">
                  <CardContent>
                    <Typography sx={{ fontWeight: 700 }}>
                      No earned rewards yet
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Referral and verified-visit offers will show progress
                      before a reward is issued.
                    </Typography>
                  </CardContent>
                </Card>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                    gap: 1.5,
                  }}
                >
                  {rewards.data.grants.map((grant) => {
                    const service = services[grant.service];
                    const usable = grant.status === "available";
                    const content = (
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack
                            direction="row"
                            spacing={1}
                            sx={{ alignItems: "center" }}
                          >
                            <RedeemRoundedIcon
                              color={usable ? "primary" : "disabled"}
                            />
                            <Typography component="h3" variant="h6">
                              {grant.campaign_name}
                            </Typography>
                          </Stack>
                          <Typography sx={{ fontWeight: 800 }}>
                            {benefit(grant)}
                          </Typography>
                          <Typography color="text.secondary">
                            {grant.facility_name}
                          </Typography>
                          <Stack
                            direction="row"
                            spacing={1}
                            useFlexGap
                            sx={{ flexWrap: "wrap" }}
                          >
                            <Chip label={service.label} size="small" />
                            <Chip
                              label={statusLabels[grant.status]}
                              size="small"
                              color={usable ? "success" : "default"}
                            />
                          </Stack>
                          <Typography variant="body2" color="text.secondary">
                            {usable
                              ? `Use by ${new Date(grant.expires_at).toLocaleDateString()}. Select the offer during checkout.`
                              : `Recorded ${new Date(grant.created_at).toLocaleDateString()}.`}
                          </Typography>
                        </Stack>
                      </CardContent>
                    );
                    return (
                      <Card key={grant.grant_id} variant="outlined">
                        {usable ? (
                          <CardActionArea
                            component={RouterLink}
                            to={service.to(grant.facility_id)}
                          >
                            {content}
                          </CardActionArea>
                        ) : (
                          content
                        )}
                      </Card>
                    );
                  })}
                </Box>
              )}
            </Stack>

            {rewards.data.referral_codes.length > 0 ? (
              <Stack
                component="section"
                spacing={1.5}
                aria-labelledby="share-referrals-heading"
              >
                <Typography
                  id="share-referrals-heading"
                  component="h2"
                  variant="h5"
                >
                  Share with friends
                </Typography>
                {rewards.data.referral_codes.map((referral) => (
                  <Card key={referral.code} variant="outlined">
                    <CardContent>
                      <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={1.5}
                        sx={{ alignItems: { sm: "center" } }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={{ fontWeight: 800 }}>
                            {referral.campaign_name}
                          </Typography>
                          <Typography variant="body2">
                            {referral.facility_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Both players earn only after the friend completes
                            the verified service.
                          </Typography>
                        </Box>
                        <Chip
                          label={referral.code}
                          sx={{ fontFamily: "monospace", fontWeight: 800 }}
                        />
                        {referral.status === "active" &&
                        rewards.data.referral_participation_allowed ? (
                          <Button
                            startIcon={<ContentCopyRoundedIcon />}
                            onClick={() => void copy(referral.code)}
                          >
                            {copied === referral.code ? "Copied" : "Copy"}
                          </Button>
                        ) : null}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : null}

            {rewards.data.referral_claims.length > 0 ? (
              <Stack
                component="section"
                spacing={1.5}
                aria-labelledby="claimed-referrals-heading"
              >
                <Typography
                  id="claimed-referrals-heading"
                  component="h2"
                  variant="h5"
                >
                  Claimed referrals
                </Typography>
                {rewards.data.referral_claims.map((referral) => (
                  <Card key={referral.claim_id} variant="outlined">
                    <CardContent>
                      <Stack
                        direction="row"
                        spacing={2}
                        sx={{ justifyContent: "space-between" }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 800 }}>
                            {referral.campaign_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {referral.facility_name} ·{" "}
                            {services[referral.service].label}
                          </Typography>
                        </Box>
                        <Chip
                          label={statusLabels[referral.status]}
                          size="small"
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            ) : null}
          </>
        ) : null}
      </Stack>
    </Page>
  );
}
